import { useId } from 'react';
import PropTypes from 'prop-types';
import { Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

/**
 * Toggleable sibling of AllInPricePill for the shopping surfaces (search
 * results + booking options). ON (the default) shows all-in totals with
 * taxes & fees; OFF switches the cards to raw base fares, relabeled "fare"
 * so the smaller number never reads as a total. The preference persists via
 * useAllInPrices, so a choice made on search sticks through booking options.
 * Checkout never toggles — the Review page always shows the real trip total.
 */
export default function AllInPriceToggle({ checked, onCheckedChange, className, compact = false }) {
  if (compact) return <CompactAllInPriceToggle checked={checked} onCheckedChange={onCheckedChange} className={className} />;
  return (
    <label
      className={cn(
        'inline-flex cursor-pointer select-none items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.04] py-1.5 pl-3.5 pr-3',
        checked && 'shadow-[0_0_18px_-6px_rgba(52,211,153,0.25)]',
        className,
      )}
    >
      <span key={checked ? 'allin' : 'fare'} className="text-[13px] font-normal leading-none text-white/85 transform-gpu">
        {checked ? 'Grand total — taxes & fees included' : 'Base fares — before taxes & fees'}
      </span>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        aria-label="Show full prices with taxes and fees"
        className="data-[state=checked]:bg-emerald-500/80"
      />
    </label>
  );
}

/**
 * The phone version, sharing a line with the speed stat (founder, 10-22):
 * just "Grand total" (or "Base fares"), with an (i) that tells what it
 * includes. The words are the switch's label, so tapping them still flips
 * it; the (i) sits outside that label, so tapping it never does.
 */
function CompactAllInPriceToggle({ checked, onCheckedChange, className }) {
  const switchId = useId();
  return (
    <div
      className={cn(
        'inline-flex select-none items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] py-1.5 pl-3 pr-3',
        checked && 'shadow-[0_0_18px_-6px_rgba(52,211,153,0.25)]',
        className,
      )}
    >
      <label htmlFor={switchId} className="cursor-pointer whitespace-nowrap text-[13px] font-normal leading-none text-white/85">
        {/* Under 375px wide the short word keeps both pills on one line. */}
        <span className="min-[375px]:hidden">{checked ? 'Total' : 'Fares'}</span>
        <span className="hidden min-[375px]:inline">{checked ? 'Grand total' : 'Base fares'}</span>
      </label>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={checked ? 'What the grand total includes' : 'What base fares leave out'}
            className="-m-1.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white/45 transition-colors hover:text-white/80"
          >
            <Info className="h-3.5 w-3.5" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="center"
          sideOffset={8}
          className="w-auto rounded-xl border-white/10 bg-brand-950/95 px-3 py-2 text-xs font-normal text-white/85 shadow-2xl backdrop-blur-xl"
        >
          {checked ? 'Taxes & fees included' : 'Before taxes & fees'}
        </PopoverContent>
      </Popover>
      <Switch
        id={switchId}
        checked={checked}
        onCheckedChange={onCheckedChange}
        aria-label="Show full prices with taxes and fees"
        className="data-[state=checked]:bg-emerald-500/80"
      />
    </div>
  );
}

const togglePropTypes = {
  /** All-in totals (true) or base fares (false). */
  checked: PropTypes.bool,
  onCheckedChange: PropTypes.func,
  className: PropTypes.string,
};

AllInPriceToggle.propTypes = {
  ...togglePropTypes,
  /** Phones: "Grand total" with an (i) instead of the full sentence. */
  compact: PropTypes.bool,
};

CompactAllInPriceToggle.propTypes = togglePropTypes;
